// ./src/contexts/OrganizerProfilesContext/OrganizerProfilesContext.tsx
//
// Thin context wrapper around the useOrganizerProfiles hook (ROADMAP A2b #54).
//
// Mounted APP-WIDE (App.tsx) rather than on the Library page, because the profile
// PICKER is what makes the feature useful and it lives elsewhere: on Details &
// Settings and in the create-event wizard. A provider on the management page would
// leave both of those with nothing to pick from.
//
// Two accessors are exported:
//   useOrganizerProfilesContext()         — throws if no provider
//   useOptionalOrganizerProfilesContext() — returns null (for components that can work without)

import React, { createContext, useContext } from "react";

import {
  useOrganizerProfiles,
  UseOrganizerProfilesOptions,
  UseOrganizerProfilesReturn,
} from "@/hooks/useOrganizerProfiles";

// ============================================
// Context
// ============================================

const OrganizerProfilesContext = createContext<UseOrganizerProfilesReturn | null>(null);

// ============================================
// Provider
// ============================================

export interface OrganizerProfilesProviderProps extends UseOrganizerProfilesOptions {
  children: React.ReactNode;
}

export const OrganizerProfilesProvider: React.FC<OrganizerProfilesProviderProps> = ({
  children,
  ...options
}) => {
  const value = useOrganizerProfiles(options);

  return (
    <OrganizerProfilesContext.Provider value={value}>
      {children}
    </OrganizerProfilesContext.Provider>
  );
};

// ============================================
// Strict accessor — throws when no provider
// ============================================

export const useOrganizerProfilesContext = (): UseOrganizerProfilesReturn => {
  const context = useContext(OrganizerProfilesContext);

  if (!context) {
    throw new Error(
      "useOrganizerProfilesContext must be used within an <OrganizerProfilesProvider>. " +
      "Wrap your component tree with <OrganizerProfilesProvider> to fix this."
    );
  }

  return context;
};

// ============================================
// Safe accessor — returns null when no provider
// ============================================

export const useOptionalOrganizerProfilesContext = (): UseOrganizerProfilesReturn | null => {
  return useContext(OrganizerProfilesContext);
};
